/* ==========================================================================
   MP — Code Redeem  (v1)

   Applies a discount code without leaving the page.

   Shopify's /discount/<CODE> endpoint is a redirect that sets a first-party
   `discount_code` cookie. Fetch it same-origin instead of navigating to it
   and the cookie still lands, so the code is on the cart and the shopper
   never moves. Proved against this store on 2026-09-22: a $4.50 cart went
   from total_price 450 to 405 with cart_level_discount_applications naming
   WELCOME10, with no navigation. `?redirect=/cart.js` makes the response the
   cart JSON itself, so one request both applies the code and tells us what
   it was worth.

   The button is a real <a href="/discount/CODE?redirect=/cart">. Without
   this file it still works — it just navigates, which is the behaviour the
   brief asked for as the fallback. Everything below is an upgrade on top of
   a link that already functions.

   No dependency on the signup pill. It binds to [data-mp-code-redeem]
   wherever that appears.
   ========================================================================== */
(function () {
  'use strict';

  var VERSION = 'v1';
  var COPY_RESET_MS = 2000;

  function rootPath() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (!r) return '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function discountUrl(code, redirectTo) {
    return rootPath() + 'discount/' + encodeURIComponent(code) + '?redirect=' + redirectTo;
  }

  /* Shopify writes the cookie unencoded and preserves the case it was given.
     Compare case-insensitively: the endpoint accepts welcome10 and the
     merchant may have typed the setting either way. */
  function cookieHasCode(code) {
    var want = String(code).toLowerCase();
    var parts = document.cookie ? document.cookie.split(';') : [];
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i].trim();
      if (p.slice(0, 14).toLowerCase() === 'discount_code=') {
        var got = p.slice(14);
        try {
          got = decodeURIComponent(got);
        } catch (e) {
          /* a malformed cookie is not our problem; compare it raw */
        }
        if (got.toLowerCase() === want) return true;
      }
    }
    return false;
  }

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    /* iOS below 13.4 and any non-secure context. execCommand needs the node
       in the document and visible enough to select, hence the off-screen
       positioning rather than display:none. */
    return new Promise(function (resolve, reject) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-9999px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      var ok = false;
      try {
        ok = document.execCommand('copy');
      } catch (e) {
        ok = false;
      }
      document.body.removeChild(ta);
      ok ? resolve() : reject(new Error('copy failed'));
    });
  }

  function MPCodeRedeem(root) {
    this.root = root;
    var d = root.dataset;

    this.code = (d.mpCode || '').trim();
    this.action = d.mpCodeAction || 'apply';
    this.cta = root.querySelector('[data-mp-code-apply]');
    this.ctaLabel = root.querySelector('[data-mp-code-cta-label]');
    this.chip = root.querySelector('[data-mp-code-copy]');
    this.chipLabel = root.querySelector('[data-mp-code-copy-label]');
    this.status = root.querySelector('[data-mp-code-status]');

    this.text = {
      redeem: d.mpRedeemLabel || 'Redeem',
      applied: d.mpAppliedLabel || 'Applied',
      appliedNote: d.mpAppliedText || '',
      copied: d.mpCopiedLabel || 'Copied',
      failed: d.mpFailedText || ''
    };

    this.busy = false;
    this.applied = false;
    this.copyTimer = null;

    root.setAttribute('data-mp-redeem-js', VERSION);

    if (this.cta) {
      this.cta.addEventListener('click', this.onApply.bind(this));
    }
    if (this.chip && this.chip.tagName === 'BUTTON') {
      this.chip.addEventListener('click', this.onCopy.bind(this));
    }

    /* Already carrying this code from an earlier visit or an earlier click:
       say so on arrival rather than inviting a second, pointless request. */
    if (this.code && cookieHasCode(this.code)) this.markApplied(null, { silent: true });
  }

  MPCodeRedeem.prototype.setStatus = function (message) {
    if (!this.status) return;
    if (!message) {
      this.status.hidden = true;
      this.status.textContent = '';
      return;
    }
    this.status.textContent = message;
    this.status.hidden = false;
  };

  MPCodeRedeem.prototype.markApplied = function (cart, opts) {
    var silent = opts && opts.silent;
    this.applied = true;
    this.busy = false;
    this.root.setAttribute('data-mp-applied', 'true');
    this.root.removeAttribute('data-mp-busy');
    if (this.ctaLabel) this.ctaLabel.textContent = this.text.applied;
    if (this.cta) this.cta.setAttribute('aria-disabled', 'true');

    var note = this.text.appliedNote;
    /* When the cart already has something in it we can be specific about the
       saving. An empty cart still gets the code — the cookie is independent
       of the cart — so the generic line has to make sense on its own. */
    if (cart && cart.total_discount > 0 && window.Shopify && window.Shopify.formatMoney) {
      try {
        note = window.Shopify.formatMoney(cart.total_discount) + ' off — ' + note;
      } catch (e) {
        /* leave the plain note */
      }
    }
    if (!silent) this.setStatus(note);
  };

  MPCodeRedeem.prototype.onApply = function (event) {
    if (this.applied) {
      event.preventDefault();
      return;
    }
    if (!this.code || this.action === 'link') return; /* let the link navigate */
    if (this.busy) {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    /* The theme editor renders the thank-you state on demand for previewing.
       Firing a real discount request from it would apply the code to the
       merchant's own session, so show the outcome and stop. */
    if (window.Shopify && window.Shopify.designMode) {
      this.markApplied(null);
      return;
    }

    var self = this;
    var href = this.cta && this.cta.getAttribute('href');
    this.busy = true;
    this.root.setAttribute('data-mp-busy', 'true');

    fetch(discountUrl(this.code, '/cart.js'), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('discount request failed: ' + response.status);
        return response.json().catch(function () {
          return null;
        });
      })
      .then(function (cart) {
        /* The response resolving is not proof. An unknown code still
           redirects happily; the cookie is what actually carries the
           discount, so that is what gets checked. */
        if (!cookieHasCode(self.code)) throw new Error('discount cookie not set');
        self.markApplied(cart);
        document.dispatchEvent(
          new CustomEvent('mp:discount-applied', {
            detail: { code: self.code, cart: cart }
          })
        );
        if (self.action === 'apply_cart') window.location.href = rootPath() + 'cart';
      })
      .catch(function (error) {
        self.busy = false;
        self.root.removeAttribute('data-mp-busy');
        if (window.console && console.warn) console.warn('[mp-code-redeem]', error);
        /* Navigating to the same endpoint is the thing that was going to
           happen without this file at all. Better a page change than a
           button that quietly does nothing. */
        if (href) window.location.href = href;
        else if (self.text.failed) self.setStatus(self.text.failed);
      });
  };

  MPCodeRedeem.prototype.onCopy = function () {
    var self = this;
    if (!this.code || !this.chipLabel) return;
    copyText(this.code).then(
      function () {
        if (self.copyTimer) window.clearTimeout(self.copyTimer);
        self.chipLabel.textContent = self.text.copied;
        self.copyTimer = window.setTimeout(function () {
          self.chipLabel.textContent = self.code;
          self.copyTimer = null;
        }, COPY_RESET_MS);
      },
      function () {
        /* Clipboard refused (permissions, or a browser that wants a
           different gesture). Select the code so it can be copied by hand
           instead of leaving the shopper with nothing. */
        if (!window.getSelection || !document.createRange) return;
        var range = document.createRange();
        range.selectNodeContents(self.chipLabel);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    );
  };

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-mp-code-redeem]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].__mpCodeRedeem) continue;
      nodes[i].__mpCodeRedeem = new MPCodeRedeem(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  window.MPCodeRedeem = MPCodeRedeem;
})();